<template>
	<b-row class="m-0">
		<b-col class="p-0">
			<fieldset :disabled="isFormDisabled">
				<b-row class="m-0 mb-2">
					<b-col lg="6" md="12">
						<div class="mh-profile-label">
							<div class="mh-img-wrap">
								<ProfileImage :imageUrl="profilePicture" />
								<span class="mh-user-online"></span>
							</div>
							<span class="mh-title">{{profile.email}}</span>
						</div>
					</b-col>
				</b-row>
				<b-row v-if="isEmployee && profile.isadmin" class="m-0">
					<b-col cols="auto" class="mb-2">
						<input type="checkbox" id="makeShiftAvailable" v-model="makeShiftAvailable" />
						<label for="makeShiftAvailable">Make this shift available</label>
					</b-col>
				</b-row>
				<b-row v-if="isEmployee" class="m-0">
					<b-col cols="auto" class="mb-2">
						<input type="checkbox" id="allDay" v-model="allDay" @change="onAllDayChange" /> <label for="allDay">All Day</label>
					</b-col>
					<b-col cols="auto">
						<input type="checkbox" id="repeat" v-model="repeat" /> <label for="repeat">Repeat</label>
					</b-col>
				</b-row>
				<b-row class="m-0">
					<b-col lg="6" md="12">
						<label class="mh-label required">Service Date</label>
						<div class="mh-time-entry">
							<div class="mh-datepicker">
								<datepicker v-model="serviceDate" :disabled-dates="{ to: moment().add(-1, 'days').toDate() }" @input="getClientServices" />
								<i class="mh-icon-calendar"></i>
							</div>
						</div>
					</b-col>
					<b-col lg="3" md="12">
						<label class="mh-label required">Time In</label>
						<div class="mh-time-entry">
							<div class="mh-timepicker">
								<TimeInput v-model="timeInTime" />
								<i class="mh-icon-watch"></i>
							</div>
						</div>
					</b-col>
					<b-col lg="3" md="12">
						<label class="mh-label required">Time Out</label>
						<div class="mh-time-entry">
							<div class="mh-timepicker">
								<TimeInput v-model="timeOutTime" />
								<i class="mh-icon-watch"></i>
							</div>
						</div>
					</b-col>
				</b-row>
				<b-row v-if="repeat" class="m-0">
					<b-col lg="3" md="12">
						<label class="mh-label required">To Date</label>
						<div class="mh-time-entry">
							<div class="mh-datepicker">
								<datepicker v-model="toDate" :disabled-dates="{ to: moment(serviceDate).toDate() }" />
								<i class="mh-icon-calendar"></i>
							</div>
						</div>
					</b-col>
					<b-col lg="3" md="12">
						<label class="mh-label required">Interval</label>
						<b-select v-model="interval">
							<b-form-select-option value="1">Daily</b-form-select-option>
							<b-form-select-option value="7">Weekly</b-form-select-option>
							<b-form-select-option value="14">Fortnightly</b-form-select-option>
							<b-form-select-option value="21">Every 3 Weeks</b-form-select-option>
							<b-form-select-option value="28">Every 4 Weeks</b-form-select-option>
							<b-form-select-option value="30">Monthly</b-form-select-option>
						</b-select>
					</b-col>
					<b-col lg="6" md="12">
						<label class="mh-label">Days</label>
						<b-form-checkbox-group v-model="days">
							<b-form-checkbox value="1">Monday</b-form-checkbox>
							<b-form-checkbox value="2">Tuesday</b-form-checkbox>
							<b-form-checkbox value="3">Wednesday</b-form-checkbox>
							<b-form-checkbox value="4">Thursday</b-form-checkbox>
							<b-form-checkbox value="5">Friday</b-form-checkbox>
							<b-form-checkbox value="6">Saturday</b-form-checkbox>
							<b-form-checkbox value="0">Sunday</b-form-checkbox>
						</b-form-checkbox-group>
					</b-col>
				</b-row>
				<b-row class="m-0">
					<b-col v-if="isEmployee" lg="6" md="12">
						<label class="mh-label required">Customer/Participant</label>
						<div class="field-with-icon">
							<vue-typeahead-bootstrap :data="customers"
													 :serializer="s => s.name"
													 @hit="($event) => {customerId = $event.id; itemId = null; $refs.itemId.inputValue = ''; getClientServices();}"
													 @keyup="($event) => {
														if($refs.customerId.inputValue == '') {
															customerId = null;
															itemId = null;
															$refs.itemId.inputValue = '';
														}
													 }"
													 autocomplete="off"
													 :showOnFocus="true"
													 :minMatchingChars="1"
													 ref="customerId"></vue-typeahead-bootstrap>
							<i class="mh-icon-search"></i>
						</div>
					</b-col>
					<b-col :lg="`${isEmployee ? '6' : '12'}`" md="12">
						<label class="mh-label required">Item</label>
						<div class="field-with-icon">
							<vue-typeahead-bootstrap :data="customerId == null ? [] : clientServices"
													 :serializer="s => s.service"
													 @hit="itemId = $event.serviceid"
													 @keyup="($event) => { $refs.itemId.inputValue != '' ? '' : itemId = null }"
													 autocomplete="off"
													 :showOnFocus="true"
													 :minMatchingChars="1"
													 :placeholder="customerId == null ? 'Select a customer first' : (clientServices.length < 1 ? 'No items available' : '')"
													 ref="itemId"></vue-typeahead-bootstrap>
							<i class="mh-icon-search"></i>
						</div>
					</b-col>
					<b-col v-if="customer != null && customer.risknotification != ''" cols="12">
						<b-alert show variant="danger"><b>Risk:</b> {{customer.risknotification}}</b-alert>
					</b-col>
					<b-col v-if="customer != null && customer.generalnotes != ''" cols="12">
						<b-alert show variant="info"><b>Formulation:</b> {{customer.generalnotes}}</b-alert>
					</b-col>
				</b-row>
			</fieldset>
			<b-row v-if="isEmployee" class="m-0">
				<b-col cols="12">
					<label class="mh-label">Activity</label>
					<b-form-input class="form-control" v-model="activity" :disabled="isTimesheetApproved"></b-form-input>
				</b-col>
			</b-row>
			<b-row v-if="isEmployee" class="m-0">
				<b-col cols="12">
					<label class="mh-label">Comments</label>
					<textarea class="form-control" v-model="additionalRosterComments" :disabled="isTimesheetApproved"></textarea>
				</b-col>
			</b-row>
			<b-row v-if="isEmployee" class="m-0">
				<b-col cols="12">
					<label class="mh-label">Notes</label>
					<textarea class="form-control" v-model="notes" :disabled="isTimesheetApproved"></textarea>
				</b-col>
			</b-row>
			<b-row v-if="profile.isadmin && notifiedStaffs.length > 0">
				<b-col cols="12 mb-3">
					<div class="blurb">
						<b>Notified Staff:</b>
						<br />
						<b-form-row v-for="(notifiedStaff, i) in notifiedStaffs" :key="i">
							<b-col>
								{{notifiedStaff.name}}
							</b-col>
						</b-form-row>
					</div>
				</b-col>
			</b-row>
			<b-row class="mr-0 ml-0 mh-action-nav">
				<b-col :cols="profile.isadmin && !isShiftAssigned && notifiedStaffs.length < 1 ? 3 : 6">
					<button class="btn btn-default btn-grey" @click="closeForm" :disabled="isFormDisabled">Close</button>
				</b-col>
				<b-col v-if="profile.isadmin && !isShiftAssigned && notifiedStaffs.length < 1" cols="6">
					<button class="btn btn-yellow" @click="isShowingPublishShiftAvailability = true">Publish Shift Availability</button>
				</b-col>
				<b-col :cols="profile.isadmin && !isShiftAssigned && notifiedStaffs.length < 1 ? 3 : 6">
					<button class="btn btn-default" @click="saveRecord" :disabled="isTimesheetApproved || isFormDisabled">Save</button>
				</b-col>
			</b-row>
		</b-col>

		<b-modal ref="forms" :visible="isShowingPublishShiftAvailability"
				 @hide="isShowingPublishShiftAvailability = false"
				 size="xl"
				 modal-class="shift-form publish-shift-availability"
				 body-class="mh-off-middle"
				 hide-footer>
			<template slot="modal-title">
				<h5 class="modal-title">Publish Shift Availability</h5>
			</template>
			<template slot="default">
				<PublishShiftAvailability :serviceScheduleIds="serviceScheduleId" @close-form="isShowingPublishShiftAvailability = false" />
			</template>
		</b-modal>
	</b-row>
</template>

<script>
	import TimeInput from "./controls/TimeInput"
	import ProfileImage from "./ProfileImage"
	import PublishShiftAvailability from "./PublishShiftAvailability"
	import moment from "moment"

	export default {
		name: 'ShiftForm',
		components: {
			TimeInput,
			ProfileImage,
			PublishShiftAvailability
		},
		props: {
			data: null
		},
		data: function () {
			return {
				serviceScheduleId: 0,
				makeShiftAvailable: false,
				allDay: false,
				repeat: false,
				serviceDate: new Date(),
				timeInTime: "",
				timeOutTime: "",
				toDate: new Date(),
				interval: 1,
				days: [],
				customerId: null,
				itemId: null,
				activity: null,
				additionalRosterComments: null,
				notes: null,
				isShowingPublishShiftAvailability: false
			}
		},
		mounted() {
			if (this.profile.isadmin && this.data.servicescheduleid)
				this.$store.dispatch("Shifts/getNotifiedStaffDetails", this.data.servicescheduleid)

			this.setFormValues()
			this.getClientServices()
		},
		watch: {
			data: {
				immediate: true,
				handler(newValue, oldValue) {
					this.setFormValues()
				}
			}
		},
		computed: {
			clientServices: function () {
				return this.$store.state.ClientServices.clientServices
			},
			profile: function () {
				return this.$store.state.Profile.profile
			},
			profilePicture: function () {
				return this.profile.profilepic
			},
			customers: function () {
				return this.$store.state.Customers.customers
			},
			customer: function () {
				return this.customers.filter(x => x.id === this.customerId)[0]
			},
			notifiedStaffs: function () {
				return this.$store.state.Shifts.notifiedStaffs
			},
			statusValues: function () {
				return this.$store.state.Shifts.statusValues
			},
			isEmployee: function () {
				return this.profile.accounttype == 2
			},
			isClient: function () {
				return this.profile.accounttype == 3
			},
			isAdding: function () {
				return this.serviceScheduleId == 0
			},
			isEditing: function () {
				return this.serviceScheduleId != 0
			},
			isFormDisabled: function () {
				return (this.isEditing && this.isClient)
					|| (this.data != null
						&& ((this.data.allStatuses == this.statusValues.completed || this.data.allStatuses == this.statusValues.cancelled)
							|| this.data.timesheetapproved == "True"))
			},
			isTimesheetApproved: function () {
				return this.data != null && this.data.timesheetapproved == "True"
			},
			isShiftAssigned: function () {
				return this.data != null && this.data.employeeid != "0"
			},
			isServiceScheduleIdValid: function () {
				if (this.serviceScheduleId == null)
					return false

				return true
			},
			isServiceDateValid: function () {
				if (this.serviceDate == null) {
					this.$store.commit("Toasts/showError", { title: "Service Date", content: "Service Date is required" })

					return false
				}

				return true
			},
			isTimeInValid: function () {
				if (this.timeInTime == "") {
					this.$store.commit("Toasts/showError", { title: "Time In", content: "Time In is required" })

					return false
				}

				return true
			},
			isTimeOutValid: function () {
				if (this.timeOutTime == "") {
					this.$store.commit("Toasts/showError", { title: "Time Out", content: "Time Out is required" })

					return false
				}

				if (moment(this.timeInTime, "HH:mm").isAfter(moment(this.timeOutTime, "HH:mm"))) {
					this.$store.commit("Toasts/showError", { title: "Time Out", content: "Cannot be less than Time In" })

					return false
				}

				return true
			},
			isCustomerIdValid: function () {
				if (this.customerId == null) {
					this.$store.commit("Toasts/showError", { title: "Customer", content: "Customer is required" })

					return false
				}

				return true
			},
			isItemIdValid: function () {
				if (this.itemId == null) {
					this.$store.commit("Toasts/showError", { title: "Item", content: "Item is required" })

					return false
				}

				return true
			},
			isActivityValid: function () {
				return true
			},
			isAdditionalRosterCommentsValid: function () {
				return true
			},
			isNotesValid: function () {
				return true
			}
		},
		methods: {
			getClientServices: function () {
				this.$store.dispatch("ClientServices/getClientServices", { customerId: this.customerId, date: moment(this.serviceDate).format("YYYY-MM-DD") })
			},
			onAllDayChange: function () {
				if (this.allDay) {
					this.timeInTime = "00:00"
					this.timeOutTime = "23:59"
				}
			},
			setFormValues: function () {
				if (this.data != null) {
					if (this.data.servicescheduleid != null)
						this.serviceScheduleId = this.data.servicescheduleid

					if (this.data.employeeid == "0")
						this.makeShiftAvailable = true

					if (this.data.servicedate != null)
						this.serviceDate = moment(this.data.servicedate, "D/MM/YYYY h:mm:ss A").toDate()

					if (this.data.timefrom != null) {
						this.timeInTime = moment(`1900-01-01 ${this.data.timefrom}`, "YYYY-MM-DD hh:mm").format("HH:mm")
					}

					if (this.data.timeuntil != null) {
						this.timeOutTime = moment(`1900-01-01 ${this.data.timeuntil}`, "YYYY-MM-DD hh:mm").format("HH:mm")
					}

					if (this.data.servicedate != null)
						this.toDate = moment(this.data.servicedate, "D/MM/YYYY h:mm:ss A").toDate()

					if (this.isEmployee) {
						if (this.data.clientid != null) {
							var client = this.customers.filter(x => x.id == this.data.clientid)[0]

							if (client != null) {
								this.customerId = client.id

								if (this.$refs.customerId != null)
									this.$refs.customerId.inputValue = client.name
							}
						}
					}
					else {
						this.customerId = this.profile.userid
					}

					if (this.data.servicetypeid != null) {
						var clientService = this.clientServices.filter(x => x.serviceid == this.data.servicetypeid)[0]

						if (clientService != null) {
							this.itemId = clientService.serviceid

							if (this.$refs.itemId != null)
								this.$refs.itemId.inputValue = clientService.service
						}
					}

					if (this.data.shiftactivity != null)
						this.activity = this.data.shiftactivity

					if (this.data.shiftcomments != null)
						this.additionalRosterComments = this.data.shiftcomments

					if (this.data.shiftnotes != null)
						this.notes = this.data.shiftnotes
				}
				else {
					this.serviceScheduleId = 0
					this.serviceDate = new Date()
					this.timeInTime = ""
					this.timeOutTime = ""
					this.customerId = this.isEmployee ? null : this.profile.userid

					if (this.$refs.customerId != null)
						this.$refs.customerId.inputValue = ""

					this.itemId = null

					if (this.$refs.itemId != null)
						this.$refs.itemId.inputValue = ""

					this.notes = null
				}
			},
			closeForm: function () {
				this.$emit("close-form")
			},
			isFormValid: function () {
				return this.isServiceScheduleIdValid
					&& this.isServiceDateValid
					&& this.isTimeInValid
					&& this.isTimeOutValid
					&& this.isCustomerIdValid
					&& this.isItemIdValid
					&& this.isActivityValid
					&& this.isAdditionalRosterCommentsValid
					&& this.isNotesValid
			},
			saveRecord: function () {
				if (!this.isFormValid())
					return

				var date = moment(this.serviceDate)
				var dateTo = moment(this.toDate)
				var totalHours = moment.duration(dateTo.diff(date)).asHours()

				var parameters = {
					id: this.serviceScheduleId,
					userId: this.makeShiftAvailable ? 0 : this.profile.userid,
					clientId: this.customerId,
					start: this.timeInTime,
					end: this.timeOutTime,
					date: date.format("YYYY-MM-DD"),
					dateTo: dateTo.format("YYYY-MM-DD"),
					totalHours: totalHours,
					serviceTypeId: this.itemId,
					billable: this.data != null ? !this.data.istimeclock : true,
					days: this.repeat ? this.days.join() : "",
					interval: this.repeat ? this.interval : 0,
					status: this.isEmployee ? (this.makeShiftAvailable ? 4 : 1) : 0,
					shiftNotes: this.notes,
					cancellationPolicyId: "0",
					activity: this.activity,
					additionalRosterComments: this.additionalRosterComments,
					SendEmailNotification: false,
					CreateShiftWithErrors: true
				}

				var $this = this

				this.$store.dispatch("Shifts/saveShift", parameters).then(function (response) {
					$this.$emit("save-form", response)

					$this.serviceScheduleId = response.shiftids.split(",")[0]
				})
			}
		}
	}
</script>
<style>
	.shift-form.publish-shift-availability .modal-header {
		background-color: #d49f00;
	}
</style>

<style scoped>
	.mh-time-entry .mh-datepicker {
		width: 100% !important;
	}
</style>